/**
 * Canonical Placeholder Vocabulary resolver for SmartPhrase insert into a Note.
 *
 * Replaces master-list tokens at insert time. Inserted text is ordinary Note
 * content (a copy). SmartPhrase storage is unchanged. Missing values become
 * empty strings. Unknown, reserved, and report-shell-only tokens are left as-is.
 *
 * Do not use this inside Generic Report Designer / resolveReportHtml.
 */
import {
  appointmentStatusLabel,
  appointmentVisitType,
  confirmationStatusLabel,
  formatAppointmentTime,
  providerDisplay,
  referringProviderDisplay,
} from "@/lib/appointmentApi";
import {
  buildNoteReportPlaceholderValues,
  reportDate,
  reportText,
  telecomValue,
} from "@/lib/noteReportShell";
import {
  MASTER_PLACEHOLDER_TOKENS,
  canonicalPlaceholderName,
} from "@/lib/placeholderVocabulary";
import {
  ageFromBirthDate,
  chartPatientFields,
  encounterClassLabel,
  encounterDate,
  encounterLocation,
  encounterNumber,
  encounterStatusLabel,
  formatAddress,
  patientEmail,
  patientFullName,
  patientPhone,
} from "@/lib/patientChart";
import {
  formatAllergiesPlaceholder,
  formatPmhPlaceholder,
  formatProblemsPlaceholder,
  formatSurgicalHistoryPlaceholder,
  nkdaPlaceholderText,
} from "@/lib/patientSummary";
import { soapValuesFromContext } from "@/lib/soapFields";

export const CANONICAL_PLACEHOLDER_RE = /@([A-Za-z][A-Za-z0-9]*)@/g;

function isClinicalContext(value) {
  return Boolean(
    value &&
      (value.patient ||
        value.fields ||
        value.encounter ||
        value.note ||
        value.diagnoses ||
        value.chartLists ||
        value.medicalHistory ||
        value.allergies ||
        value.problems ||
        value.reportContext),
  );
}

function isLegacyPatientValues(value) {
  if (!value || typeof value !== "object") return false;
  if (isClinicalContext(value)) return false;
  if (value.name || value.birthDate || value.identifier || value.telecom) return false;
  return value.patientName != null || value.age != null;
}

/** Accept a clinical context, a FHIR Patient, or legacy { patientName, age }. */
export function normalizePlaceholderContext(input = {}) {
  if (!input) return {};
  if (isClinicalContext(input)) return input;
  if (isLegacyPatientValues(input)) {
    return {
      fields: {
        name: input.patientName || "",
        age: input.age,
      },
    };
  }
  return { patient: input };
}

function emptyMasterValues() {
  const out = {};
  for (const token of MASTER_PLACEHOLDER_TOKENS) out[token] = "";
  return out;
}

function nameParts(patient) {
  const n = patient?.name?.[0] || {};
  return {
    firstname: String(n.given?.[0] || ""),
    middlename: String(n.given?.[1] || ""),
    lastname: String(n.family || ""),
    suffix: String(n.suffix?.[0] || ""),
  };
}

function telecomByUse(patient, system, use) {
  const list = patient?.telecom || [];
  const match = list.find((item) => item?.system === system && item?.use === use);
  return reportText(match?.value);
}

function addressParts(patient) {
  const addr = patient?.address?.[0] || {};
  const line = Array.isArray(addr.line) ? addr.line : [];
  const city = reportText(addr.city);
  const state = reportText(addr.state);
  const postal = reportText(addr.postalCode);
  const cityState = [city, state].filter(Boolean).join(", ");
  const citystatezip = [cityState, postal].filter(Boolean).join(" ").trim();
  return {
    addressline1: reportText(line[0]),
    addressline2: reportText(line[1]),
    city,
    state,
    postalcode: postal,
    country: reportText(addr.country),
    citystatezip,
  };
}

function identifierValue(resource, predicate) {
  const ident = (resource?.identifier || []).find(predicate);
  return reportText(ident?.value);
}

function orgIdentifier(organization, { system, code, oid } = {}) {
  return identifierValue(organization, (item) => {
    if (system && item?.system === system) return true;
    if (oid && String(item?.system || "").includes(oid)) return true;
    return (item?.type?.coding || []).some((c) => c.code === code);
  });
}

function clockDate(now) {
  if (now instanceof Date && !Number.isNaN(now.getTime())) {
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const yyyy = now.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }
  return reportDate(now);
}

function clockDateTime(now) {
  if (!(now instanceof Date) || Number.isNaN(now.getTime())) return reportDate(now);
  const date = clockDate(now);
  let hours = now.getHours();
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const suffix = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${date} ${hours}:${minutes} ${suffix}`;
}

function timeOf(iso) {
  return reportText(formatAppointmentTime(iso));
}

function addMinutesIso(iso, minutes) {
  if (!iso || minutes == null || minutes === "") return "";
  const stamp = Date.parse(iso);
  if (Number.isNaN(stamp)) return "";
  return new Date(stamp + Number(minutes) * 60000).toISOString();
}

function diagnosisLabel(item) {
  if (!item?.code) return "";
  const description = item.description || item.display || "";
  if (description) return `${item.code} — ${description}`;
  return item.code;
}

function primaryDiagnosis(diagnoses = []) {
  const rows = (diagnoses || []).filter((item) => item?.code);
  return rows.find((item) => item.isPrimary === true) || rows[0] || null;
}

function noteStatusLabelValue(note) {
  if (!note) return "";
  const version = note.currentVersion;
  if (version?.status === "draft") return "draft";
  if (version?.status === "signed") return "signed";
  if (note.status === "signed") return "signed";
  return "draft";
}

function cptList(note, extraCpt) {
  const fromNote = note?.currentVersion?.cpt || extraCpt || [];
  return (fromNote || [])
    .filter((item) => item?.code)
    .map((item) => {
      const display = item.display || item.description || "";
      return display ? `${item.code} — ${display}` : item.code;
    })
    .filter(Boolean)
    .join("; ");
}

function chartListsFromContext(context = {}) {
  const lists = context.chartLists || {};
  return {
    medicalHistory: context.medicalHistory || lists.medicalHistory || [],
    surgicalHistory: context.surgicalHistory || lists.surgicalHistory || [],
    allergies: context.allergies || lists.allergies || [],
    allergyReview: context.allergyReview || lists.allergyReview || null,
    noKnownAllergies: context.noKnownAllergies ?? lists.noKnownAllergies ?? false,
    problems: context.problems || lists.problems || [],
  };
}

function emergencyParts(fields, patient) {
  const contact = fields?.contact || {};
  const given = patient?.contact?.[0]?.name?.given || [];
  const family = patient?.contact?.[0]?.name?.family || "";
  return {
    emergencyname: reportText(contact.name),
    emergencyfirstname: reportText(given[0]),
    emergencylastname: reportText(family),
    emergencyrelationship: reportText(contact.relationship),
    emergencyphone: reportText(contact.phone),
  };
}

/**
 * Build canonical token values from the Encounter Note context.
 * Keys are canonical names without `@`.
 */
export function buildCanonicalPlaceholderValues(input = {}) {
  const context = normalizePlaceholderContext(input);
  const patient = context.patient || null;
  const fields = context.fields || (patient ? chartPatientFields(patient) : null);
  const encounter = context.encounter || null;
  const note = context.note || null;
  const diagnoses = context.diagnoses || note?.currentVersion?.diagnoses || [];
  const reportContext = context.reportContext || {};
  const appointment = reportContext.appointment || context.appointment || null;
  const organization = reportContext.organization || context.organization || null;
  const location = reportContext.location || context.location || null;
  const providerUser = reportContext.providerUser || context.providerUser || null;
  const now = context.now instanceof Date ? context.now : new Date();
  const chartLists = chartListsFromContext(context);

  const values = emptyMasterValues();
  const report = buildNoteReportPlaceholderValues({
    fields,
    encounter,
    note,
    organization,
    location,
    appointment,
    providerUser,
    now,
  });

  for (const [key, value] of Object.entries(report)) {
    if (MASTER_PLACEHOLDER_TOKENS.has(key)) {
      values[key] = reportText(value);
    }
  }

  const soap = soapValuesFromContext({
    fields,
    encounter,
    noteDiagnoses: diagnoses,
  });

  const parts = nameParts(patient);
  const addr = addressParts(patient);
  const emergency = emergencyParts(fields, patient);
  const age =
    fields?.age != null && fields.age !== ""
      ? String(fields.age)
      : (() => {
          const years = ageFromBirthDate(patient?.birthDate || fields?.birthDate);
          return years == null ? "" : String(years);
        })();

  const fullname = reportText(fields?.name) || reportText(patientFullName(patient));

  const providerSplitName = String(providerUser?.firstName || providerUser?.lastName || "")
    ? {
        first: reportText(providerUser?.firstName),
        last: reportText(providerUser?.lastName),
      }
    : {
        first: "",
        last: "",
      };

  const primary = primaryDiagnosis(diagnoses);
  const encounterStart = encounterDate(encounter);
  const encounterEnd = encounter?.period?.end || "";

  Object.assign(values, {
    fullname,
    firstname: parts.firstname,
    middlename: parts.middlename,
    lastname: parts.lastname,
    suffix: parts.suffix,
    mrn: reportText(fields?.mrn || soap.mrn),
    ssn: reportText(fields?.ssn),
    gender: reportText(fields?.gender || soap.gender),
    birthsex: reportText(fields?.birthsex),
    dob: fields?.birthDate || patient?.birthDate ? reportDate(fields?.birthDate || patient?.birthDate) : "",
    age,
    maritalstatus: reportText(fields?.marital),
    language: reportText(fields?.language),
    race: reportText(fields?.race),
    ethnicity: reportText(fields?.ethnicity),
    genderidentity: reportText(fields?.genderIdentity),
    pronouns: reportText(fields?.pronouns),
    patientstatus: fields ? (fields.active === false ? "Inactive" : "Active") : "",
    deceased: fields?.deceased ? "Deceased" : "",
    deceaseddate: reportDate(fields?.deceasedDate),
    mobilephone: telecomByUse(patient, "phone", "mobile"),
    homephone: telecomByUse(patient, "phone", "home"),
    phone: reportText(fields?.phone) || reportText(patientPhone(patient)),
    email: reportText(fields?.email) || reportText(patientEmail(patient)),
    address: reportText(fields?.address) || reportText(formatAddress(patient)),
    ...addr,
    ...emergency,
    encounternumber: reportText(encounter ? encounterNumber(encounter) : ""),
    dateofservice: reportDate(encounterStart) || reportText(soap.dos),
    timeofservice: timeOf(encounterStart),
    encounterenddate: reportDate(encounterEnd),
    encounterstatus: reportText(encounterStatusLabel(encounter?.status)),
    encounterclass: reportText(encounterClassLabel(encounter)),
    encountertype: reportText(soap.encountertype),
    chiefcomplaint: reportText(soap.chiefcomplaint),
    encounterlocation: reportText(encounterLocation(encounter)) || values.locationname,
    serviceprovider: reportText(encounter?.serviceProvider?.display),
    appointmentdate: reportDate(appointment?.start),
    appointmenttime: timeOf(appointment?.start),
    appointmentendtime: timeOf(appointment?.end || addMinutesIso(appointment?.start, appointment?.minutesDuration)),
    appointmentduration:
      appointment?.minutesDuration == null || appointment?.minutesDuration === ""
        ? ""
        : String(appointment.minutesDuration),
    appointmentstatus: reportText(appointmentStatusLabel(appointment?.status)),
    confirmationstatus: reportText(confirmationStatusLabel(appointment?.confirmationStatus)),
    appointmentreason: reportText(
      appointment?.reason ||
        appointment?.reasonCode?.[0]?.text ||
        appointment?.reasonCode?.[0]?.coding?.[0]?.display,
    ),
    patientinstruction: reportText(appointment?.patientInstruction),
    appointmentcomment: reportText(appointment?.comment),
    resourcename: reportText(appointment?.resourceName),
    appointmentvisittype: reportText(appointmentVisitType(appointment)),
    provider: values.provider || reportText(providerDisplay(appointment)),
    providerfirstname: providerSplitName.first,
    providerlastname: providerSplitName.last,
    providertitle: values.providertitle || reportText(providerUser?.title),
    providerphone: reportText(providerUser?.phone),
    referringprovider: reportText(referringProviderDisplay(appointment)),
    referringcorrespondence: reportText(referringProviderDisplay(appointment)),
    practicenpi: orgIdentifier(organization, { code: "NPI", system: "http://hl7.org/fhir/sid/us-npi" }),
    taxid: orgIdentifier(organization, { oid: "2.16.840.1.113883.4.4", code: "TAX" }),
    clia: orgIdentifier(organization, { oid: "2.16.840.1.113883.4.7", code: "CLIA" }),
    practiceemail: telecomValue(organization, "email"),
    locationemail: telecomValue(location, "email"),
    locationtype: reportText(location?.type?.[0]?.text || location?.type?.[0]?.coding?.[0]?.display || location?.type),
    locationdescription: reportText(location?.description),
    notetype: reportText(note?.noteType?.displayName),
    notetitle: reportText(note?.title),
    notestatus: note ? reportText(noteStatusLabelValue(note)) : "",
    noteauthor: reportText(note?.originalAuthorDisplay || note?.currentVersion?.authorDisplay),
    notesignedby: reportText(note?.currentVersion?.signedByDisplay),
    notesignedat: reportDate(note?.currentVersion?.signedAt),
    noteversion:
      note?.currentVersion?.versionNumber == null ? "" : String(note.currentVersion.versionNumber),
    diagnoses: reportText(soap.diagnoses),
    primarydiagnosis: reportText(diagnosisLabel(primary)),
    primarydiagnosiscode: reportText(primary?.code),
    cpt: cptList(note, context.cpt),
    pmh: formatPmhPlaceholder(chartLists.medicalHistory),
    surgicalhistory: formatSurgicalHistoryPlaceholder(chartLists.surgicalHistory),
    allergies: formatAllergiesPlaceholder(chartLists.allergies, chartLists),
    nkda: nkdaPlaceholderText(chartLists),
    problems: formatProblemsPlaceholder(chartLists.problems),
    todaysdate: clockDate(now),
    printedat: clockDateTime(now),
  });

  if (!values.practicealias && organization?.alias?.[0]) {
    values.practicealias = reportText(organization.alias[0]);
  }
  if (organization?.contact?.[0]) {
    const contact = organization.contact[0];
    const contactName = [...(contact.name?.given || []), contact.name?.family].filter(Boolean).join(" ");
    values.practicecontactname = reportText(contactName || contact.name?.text);
    values.practicecontactphone = reportText(
      (contact.telecom || []).find((item) => item.system === "phone")?.value,
    );
  }
  const practiceAddr = Array.isArray(organization?.address) ? organization.address[0] : organization?.address;
  if (practiceAddr) {
    values.practiceaddressline1 = reportText(practiceAddr.line?.[0]);
    values.practiceaddressline2 = reportText(practiceAddr.line?.[1]);
    values.practicecity = reportText(practiceAddr.city);
    values.practicestate = reportText(practiceAddr.state);
    values.practicepostalcode = reportText(practiceAddr.postalCode);
    values.practicecountry = reportText(practiceAddr.country);
  }
  const locAddr = Array.isArray(location?.address) ? location.address[0] : location?.address;
  if (locAddr) {
    values.locationaddressline1 = reportText(locAddr.line?.[0]);
    values.locationaddressline2 = reportText(locAddr.line?.[1]);
    values.locationcity = reportText(locAddr.city);
    values.locationstate = reportText(locAddr.state);
    values.locationpostalcode = reportText(locAddr.postalCode);
    values.locationcountry = reportText(locAddr.country);
  }

  return values;
}

export function resolveCanonicalPlaceholders(text, context = {}) {
  if (!text) return "";
  const values = buildCanonicalPlaceholderValues(context);
  return String(text).replace(CANONICAL_PLACEHOLDER_RE, (match, token) => {
    const key = canonicalPlaceholderName(token);
    if (!MASTER_PLACEHOLDER_TOKENS.has(key)) return match;
    const value = values[key];
    return value == null ? "" : String(value);
  });
}
